/**
 * The date-range calendar: a trip's start and end, chosen in one month grid.
 *
 * Two `<input type="date">` fields named the same two dates and could not say the one thing a travel
 * date is — a run of days. Choosing a start and an end in one grid makes the span visible while it is
 * chosen: tapping 12 then 14 fills 12, 13 and 14, and "three days, two nights" is something the user
 * sees rather than something they compute from two boxes. The fields are gone; this is what replaced
 * them.
 *
 * ## Dates are UTC, and that is not a detail of style
 *
 * `"YYYY-MM-DD"` is parsed here as `Date.parse(`${iso}T00:00:00Z`)`, days are whole `86_400_000ms`
 * steps, and every calendar field is read with `getUTC*`. That is the same arithmetic `Planner.tsx`
 * (`Date.parse(`${startDate}T00:00:00Z`)`, `(end - start) / 86_400_000`) and `lib/geo.ts` already run,
 * and it is what keeps one vocabulary for a date across the app.
 *
 * The alternative is the classic date-picker bug. `new Date(2026, 9, 12)` is local midnight, and west
 * of UTC that instant is the *previous* day: a grid built from local dates marks the wrong cell, and a
 * stored `"2026-10-12"` rendered through `getDate()` shows as the 11th. `Date.parse("2026-10-12")` is
 * UTC by spec and would also be correct, but it is the one form whose timezone comes from the string's
 * shape rather than from the code, so the explicit `T00:00:00Z` is written everywhere.
 *
 * ## Two clicks, backwards is a range
 *
 * The first click sets the start and clears the end; the second sets the end. A second click *before*
 * the start swaps the two rather than reporting a backwards range, because "14 to 12" is how a user
 * fixes a start they tapped one day late, and a range the calendar refuses is a range with no way to
 * enter it. A click after a complete range starts a new one.
 *
 * ## Focus and the popover
 *
 * The trigger is a real `button` carrying `aria-expanded`/`aria-haspopup`, so the grid's existence is
 * announced rather than inferred from a shape on screen. Escape closes it and hands focus back to the
 * trigger — the control the user came from — and a click outside closes it without moving focus.
 */

import { useEffect, useLayoutEffect, useRef, useState } from "react";
import type { ReactElement } from "react";

import { copy } from "../copy.ts";

export interface RangeCalendarProps {
  /** `YYYY-MM-DD`, or "" for nothing chosen yet. */
  start: string;
  end: string;
  /** Called with the new pair. A click that has not completed a range reports the start and end: "" . */
  onChange: (start: string, end: string) => void;
  /** The accessible name of the control that opens it. */
  label: string;
}

/**
 * One day, in milliseconds.
 *
 * The same constant `Planner.tsx` divides by. UTC days are exactly this long — no daylight saving to
 * make one 23 or 25 hours — which is why the whole file can step in whole days.
 */
const DAY_MS = 86_400_000;

/**
 * Six weeks, always.
 *
 * A month needs five rows or six depending on the weekday it starts on, and a grid that changed height
 * as the user stepped through months would move the button under the thumb mid-tap. The sixth row is
 * the next month's first days, which the muted treatment already accounts for.
 */
const GRID_DAYS = 42;

/**
 * How far the month steps reach, in months, either side of today.
 *
 * A guard, not a feature: the grid is unbounded otherwise, and a user holding `‹` walks off the end of
 * the useful calendar one month at a time. A year either way covers every trip anyone plans and keeps
 * the disabled state meaningful.
 */
const MONTH_REACH = 12;

/**
 * The popover's height, measured in the browser at 390px: 24px of padding and a 2px border around a
 * 44px header, an 8px gap, and seven grid rows — a 24px weekday row and six 44px day rows, with six
 * 4px gaps between them (312px). 390px, and it does not vary with the month because the grid is always
 * six weeks.
 *
 * It is only read when deciding which side to open on, so an error of a few pixels changes nothing; it
 * is written down because the 220px copied from the places list's row menu would have opened this grid
 * downward off the bottom of a phone.
 */

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * The narrowest the popover may be, derived from the tap floor rather than chosen.
 *
 * Seven columns at the 44px this app requires of every control, six 4px gaps between them, and the
 * popover's own 12px padding twice: `7 * 44 + 6 * 4 + 2 * 12 = 356`. Below this the days are smaller than a
 * thumb, which is what happened when the popover simply inherited its parent's width — measured at 39px on
 * the trip setup screen, five under the floor this project states as a constant. Above it the field's own
 * width wins, so a wide screen gets a wide calendar and a narrow column still gets days that can be hit.
 */
const MIN_POPOVER_WIDTH = 44 * 7 + 4 * 6 + 12 * 2;

/** A stored date to UTC midnight, or null when the string is empty or is not a date. */
function toDay(iso: string): number | null {
  if (!ISO_DAY.test(iso)) return null;
  const ms = Date.parse(`${iso}T00:00:00Z`);
  return Number.isNaN(ms) ? null : ms;
}

/** UTC midnight back to the stored form. */
function toIso(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10);
}

/**
 * Today, at UTC midnight.
 *
 * Epoch 0 is itself a UTC midnight, so floor-dividing the clock by a day lands on one — no `Date`
 * built, no timezone read. `new Date().getDate()` and friends are local and would name a different day
 * for anyone not on UTC.
 */
function todayUtc(): number {
  return Math.floor(Date.now() / DAY_MS) * DAY_MS;
}

/** The `YYYY-MM` key of the month holding a day. It sorts as a string, which is how the ±12 guard compares. */
function monthOf(ms: number): string {
  return toIso(ms).slice(0, 7);
}

/** A `YYYY-MM` key shifted by whole months. */
function shiftMonth(month: string, delta: number): string {
  const months = Number(month.slice(0, 4)) * 12 + Number(month.slice(5, 7)) - 1 + delta;
  const year = Math.floor(months / 12);
  return `${String(year).padStart(4, "0")}-${String(months - year * 12 + 1).padStart(2, "0")}`;
}

/** `12/10`, for the trigger. Three call sites, so the UTC read is written once. */
function shortDay(ms: number): string {
  const date = new Date(ms);
  return copy.calendar.shortDay(date.getUTCDate(), date.getUTCMonth() + 1);
}

export function RangeCalendar({ start, end, onChange, label }: RangeCalendarProps): ReactElement {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  /** The popover itself, measured on open so its position is its own rather than its parent's. */
  const popoverRef = useRef<HTMLDivElement>(null);

  const [open, setOpen] = useState(false);
  /** Whether the grid opens above the trigger. Decided against the viewport when it opens, not guessed. */
  const [placement, setPlacement] = useState<{ top: number; left: number; width: number } | null>(null);

  const today = todayUtc();
  const minMonth = shiftMonth(monthOf(today), -MONTH_REACH);
  const maxMonth = shiftMonth(monthOf(today), MONTH_REACH);

  const startMs = toDay(start);
  const endMs = toDay(end);

  /** The month on screen. */
  const [cursor, setCursor] = useState<string>(() =>
    clampMonth(startMs !== null ? monthOf(startMs) : monthOf(today)),
  );

  /**
   * The month, held inside the reach.
   *
   * Clamping here rather than only disabling the arrows is what makes the guard a guard: a muted day in
   * the adjacent month is a second door into the same month, and it would otherwise walk past the limit
   * the arrows hold. A stored date outside the window (an old trip's start) clamps too — the range still
   * reports, it is only the grid that stays inside a year of today.
   */
  function clampMonth(month: string): string {
    if (month < minMonth) return minMonth;
    if (month > maxMonth) return maxMonth;
    return month;
  }

  useEffect(() => {
    if (!open) return;

    function onKeyDown(event: KeyboardEvent): void {
      if (event.key !== "Escape") return;
      setOpen(false);
      // Focus goes back where it came from. A dialog that closes and leaves focus on the document body
      // means the next Tab starts at the top of the page.
      triggerRef.current?.focus();
    }

    function onPointerDown(event: MouseEvent): void {
      const target = event.target;
      if (target instanceof Node && wrapperRef.current?.contains(target) !== true) setOpen(false);
    }

    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("mousedown", onPointerDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("mousedown", onPointerDown);
    };
  }, [open]);

  function toggle(): void {
    if (open) {
      setOpen(false);
      return;
    }

    // Re-centred on the chosen start every time it opens, so a grid left on some other month is not what
    // the user returns to. Before the chosen date and after it, this is the month they were looking at.
    setCursor(clampMonth(startMs !== null ? monthOf(startMs) : monthOf(today)));
    setOpen(true);
  }

  /*
   * Where the popover goes, measured against the viewport rather than against its own parent.
   *
   * It used to be `position: absolute` inside the wrapper, which is right until the field sits inside
   * something narrower than the screen. Measured on the trip setup screen at 390px, inside a card with its
   * own 16px padding: the grid had **302px** for seven columns, so a day button came out **39px wide** —
   * five pixels under this app's own 44px tap floor, which is a constant in the token layer rather than a
   * preference (`size.tapMin`). A day that is hard to hit is a day the user picks by mistake, and the date
   * is the one field on that screen everything else depends on.
   *
   * `useLayoutEffect` and not `useEffect`: the popover is already in the DOM when this runs and has not
   * been painted, so it is positioned before anyone sees it rather than jumping after the first frame.
   *
   * The width is the trigger's own width when the screen can hold it, and the viewport minus a margin when
   * it cannot — so a narrow column still gets a grid whose days are 44px, which is the number that matters.
   */
  useLayoutEffect(() => {
    if (!open) return;
    const trigger = triggerRef.current;
    const popover = popoverRef.current;
    if (trigger === null || popover === null) return;

    function update(): void {
      if (trigger === null || popover === null) return;
      const anchor = trigger.getBoundingClientRect();
      const box = popover.getBoundingClientRect();
      const inset = 16;
      const viewport = window.innerWidth;
      const width = Math.min(Math.max(anchor.width, box.width, MIN_POPOVER_WIDTH), viewport - inset * 2);
      const left = Math.min(Math.max(anchor.left, inset), viewport - inset - width);

      // Below when it fits, above when it does not and there is room, and clamped to the viewport otherwise —
      // the same order of preference the CSS-only version had, now against real numbers.
      const below = anchor.bottom + 8;
      const above = anchor.top - 8 - box.height;
      const top =
        below + box.height <= window.innerHeight - inset
          ? below
          : above >= inset
            ? above
            : Math.max(inset, window.innerHeight - inset - box.height);

      setPlacement({ top, left, width });
    }

    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [open, cursor]);

  function pick(dayMs: number): void {
    const iso = toIso(dayMs);

    // A muted day is a step into the month it belongs to, clamped like every other month move.
    if (monthOf(dayMs) !== cursor) setCursor(clampMonth(monthOf(dayMs)));

    if (startMs === null || endMs !== null) {
      // Nothing chosen, or a complete range: this click is a new start.
      onChange(iso, "");
      return;
    }

    // The second click. Backwards swaps rather than reporting `start > end`, which every day count in
    // the app would then read as a negative span.
    if (dayMs < startMs) onChange(iso, start);
    else onChange(start, iso);
  }

  const valueText =
    startMs === null
      ? copy.calendar.empty
      : endMs === null
        ? copy.calendar.halfRange(shortDay(startMs))
        : copy.calendar.range(shortDay(startMs), shortDay(endMs));

  /*
   * Monday-first. `getUTCDay()` is 0 for Sunday, and a Vietnamese week starts on Monday, so shifting by
   * six turns Sunday into 6 and Monday into 0.
   */
  const firstMs = Date.parse(`${cursor}-01T00:00:00Z`);
  const lead = (new Date(firstMs).getUTCDay() + 6) % 7;
  const gridStart = firstMs - lead * DAY_MS;
  /** `Tháng 10 năm 2026`. The `YYYY-MM` key is split rather than parsed back into a `Date`. */
  const monthText = copy.calendar.month(Number(cursor.slice(5, 7)), Number(cursor.slice(0, 4)));

  return (
    <div className="range-cal" ref={wrapperRef}>
      <button
        type="button"
        className="range-cal__trigger"
        ref={triggerRef}
        aria-haspopup="dialog"
        aria-expanded={open}
        /*
         * The label names the control and the value is read after it: `label` alone would announce
         * "Ngày của chuyến, button" and hide the one thing the control is for, which is which dates are
         * already chosen.
         */
        aria-label={copy.calendar.triggerName(label, valueText)}
        onClick={toggle}
      >
        <span className="range-cal__value">{valueText}</span>
        <span className="range-cal__chevron" aria-hidden="true">
          {open ? "▴" : "▾"}
        </span>
      </button>

      {open && (
        <div
          ref={popoverRef}
          /*
           * Geometry, not styling: where this element sits is a measurement of the trigger and the
           * viewport, which no stylesheet can make. The colours, radii and spacing are all in
           * `styles/ui.css` with everything else, and only the four numbers that depend on the page are
           * here.
           */
          style={
            placement === null
              ? undefined
              : { top: placement.top, left: placement.left, width: placement.width }
          }
          className="range-cal__popover"
          role="dialog"
          aria-label={copy.calendar.dialog}
        >
          <div className="range-cal__head">
            <button
              type="button"
              className="range-cal__nav"
              aria-label={copy.calendar.previousMonth}
              disabled={cursor <= minMonth}
              onClick={() => setCursor(shiftMonth(cursor, -1))}
            >
              ‹
            </button>
            <span className="range-cal__month">{monthText}</span>
            <button
              type="button"
              className="range-cal__nav"
              aria-label={copy.calendar.nextMonth}
              disabled={cursor >= maxMonth}
              onClick={() => setCursor(shiftMonth(cursor, 1))}
            >
              ›
            </button>
          </div>

          <div className="range-cal__grid">
            {copy.calendar.weekdays.map((weekday) => (
              <span className="range-cal__weekday" key={weekday}>
                {weekday}
              </span>
            ))}
            {Array.from({ length: GRID_DAYS }, (_, index) => gridStart + index * DAY_MS).map((dayMs) => {
              const date = new Date(dayMs);
              const muted = monthOf(dayMs) !== cursor;
              const isStart = dayMs === startMs;
              const isEnd = dayMs === endMs;
              const between = startMs !== null && endMs !== null && dayMs > startMs && dayMs < endMs;
              /*
               * The two ends are the ends whether they came in ascending order or the user tapped the
               * later day first, because `pick` swaps them as they are entered.
               */
              const chosen = isStart || isEnd;
              const classes = [
                "range-cal__day",
                muted ? "range-cal__day--muted" : "",
                between ? "range-cal__day--in-range" : "",
                chosen ? "range-cal__day--end" : "",
                dayMs === today ? "range-cal__day--today" : "",
              ]
                .filter(Boolean)
                .join(" ");

              return (
                <button
                  key={toIso(dayMs)}
                  type="button"
                  className={classes}
                  aria-label={copy.calendar.dayLabel(
                    date.getUTCDate(),
                    date.getUTCMonth() + 1,
                    date.getUTCFullYear(),
                  )}
                  aria-current={dayMs === today ? "date" : undefined}
                  aria-pressed={chosen}
                  onClick={() => pick(dayMs)}
                >
                  {date.getUTCDate()}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
